import { useCallback, useEffect, useReducer } from "react";
import {
  StripePaymentIntent,
  StripeSubscription,
  StripeSetupIntent,
} from "src/types/stripe";
import {
  GenericPath,
  useV3Create,
  useV3GenericGetData,
  useV3Update,
} from "src/api/snugtotal/estate_app";
import { useEstateAppApiContext } from "src/contexts/estateAppApi-context";
import { Household, PaymentMethod } from "src/types/snugtotal";

export interface PaymentRequiredData {
  will_price: number;
  trust_price: number;
  credit_available?: number;
  total_amount_paid?: number;
  covered_ud_ids?: string[];
}

type StripeStoreAction =
  | { type: "SET_CURRENT_PAYMENT_INTENT"; payload: StripePaymentIntent | null }
  | { type: "SET_PAYMENTS_SAVING"; payload: boolean }
  | { type: "SET_ACCOUNT_DATA_INITIAL_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: any }
  | { type: "SET_PAYMENT_REQUIRED"; payload: PaymentRequiredData }
  | { type: "SET_PAYMENT_REQUIRED_LOADING"; payload: boolean }
  | {
      type: "SET_CURRENT_ACTIVE_SUBSCRIPTION";
      payload: StripeSubscription | null;
    };

interface StripeStoreState {
  currentActiveSubscription: StripeSubscription | null;
  currentPaymentIntent: StripePaymentIntent | null;
  paymentsSaving: boolean;
  accountDataInitialLoading: boolean;
  error: any;
  paymentRequiredData: PaymentRequiredData | null;
  paymentRequiredLoading: boolean;
  paymentMethods: PaymentMethod[];
  paymentMethodsLoading: boolean;
}

const stripeStoreReducer = (
  state: StripeStoreState,
  action: StripeStoreAction
): StripeStoreState => {
  switch (action.type) {
    case "SET_CURRENT_ACTIVE_SUBSCRIPTION":
      return { ...state, currentActiveSubscription: action.payload };
    case "SET_CURRENT_PAYMENT_INTENT":
      return { ...state, currentPaymentIntent: action.payload };
    case "SET_PAYMENTS_SAVING":
      return { ...state, paymentsSaving: action.payload };
    case "SET_ACCOUNT_DATA_INITIAL_LOADING":
      return { ...state, accountDataInitialLoading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "SET_PAYMENT_REQUIRED":
      return { ...state, paymentRequiredData: action.payload };
    case "SET_PAYMENT_REQUIRED_LOADING":
      return { ...state, paymentRequiredLoading: action.payload };
    default:
      return state;
  }
};

export interface StripeStoreProps {
  ud_id: string | null;
  household: Household | null;
}

export const useStripeStore = (stripeStoreProps: StripeStoreProps) => {
  const { estateAppApiInstance } = useEstateAppApiContext();

  const [state, dispatch] = useReducer(stripeStoreReducer, {
    currentActiveSubscription: null,
    currentPaymentIntent: null,
    paymentsSaving: false,
    accountDataInitialLoading: true,
    error: null,
    paymentRequiredData: null,
    paymentRequiredLoading: false,
    paymentMethods: [],
    paymentMethodsLoading: false,
  });

  const product = !stripeStoreProps.household
    ? null
    : stripeStoreProps.household?.head_of_household?.estate_plan_foundation === "joint_trust"
    ? "JOINT_TRUST"
    : (stripeStoreProps.household?.head_of_household?.estate_plan_foundation === "single_trust" || stripeStoreProps.household?.head_of_household?.estate_plan_foundation === "two_trusts")
    ? "TRUST"
    : "WILL";

  const getPaymentRequired = useV3GenericGetData<PaymentRequiredData>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const handleCreatePayment = useV3Create<any>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const handleUpdatePayment = useV3Update<any>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );

  const handleCreateSubscription = useV3Create<StripeSubscription>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const handleRetrieveSubscription = useV3GenericGetData<StripeSubscription>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );

  // const handleRetrievePaymentMethods = useV3GenericGetData<any>("/v3/{ud_id}/{entity}");

  const createSubscription = useCallback(
    async (ud_id: string) => {
      try {
        const params = {
          entity: "membersihips/subscription/",
          ud_id: ud_id,
        };
        const response = await handleCreateSubscription(params, {});
        dispatch({
          type: "SET_CURRENT_ACTIVE_SUBSCRIPTION",
          payload: response,
        });
        return response;
      } catch (err) {
        dispatch({ type: "SET_ERROR", payload: err });
        return null;
      }
    },
    [handleCreateSubscription]
  );

  const cancelSubscription = useCallback(
    async (ud_id: string) => {
      try {
        const path = `/v3/${ud_id}/memberships/cancel/`;
        await estateAppApiInstance.post(path, {});
        dispatch({ type: "SET_CURRENT_ACTIVE_SUBSCRIPTION", payload: null });
      } catch (err) {
        dispatch({ type: "SET_ERROR", payload: err });
      }
    },
    [estateAppApiInstance]
  );

  const retrieveSubscription = useCallback(
    async (ud_id: string) => {
      try {
        const params = {
          entity: "memberships/subscription/",
          ud_id: ud_id,
        };
        const response = await handleRetrieveSubscription(params);
        dispatch({
          type: "SET_CURRENT_ACTIVE_SUBSCRIPTION",
          payload: response,
        });
        return response;
      } catch (err) {
        dispatch({ type: "SET_ERROR", payload: err });
        return null;
      }
    },
    [handleRetrieveSubscription]
  );

  const createSetupIntent = useCallback(
    async (ud_id: string): Promise<StripeSetupIntent | null> => {
      try {
        const path = `/v3/${ud_id}/memberships/setup-intent/`;
        const response = await estateAppApiInstance.post(path, {});
        // Backend returns { data: { client_secret, setup_intent_id } }
        return response.data.data as StripeSetupIntent;
      } catch (err) {
        dispatch({ type: "SET_ERROR", payload: err });
        return null;
      }
    },
    [estateAppApiInstance]
  );

  const createTrialSubscription = useCallback(
    async (ud_id: string, payment_method_id: string, price_id?: string) => {
      try {
        const path = `/v3/${ud_id}/memberships/trial-subscription/`;
        const body = {
          payment_method_id,
          price_id,
        };
        const response = await estateAppApiInstance.post(path, body);
        const subscription = response.data as StripeSubscription;
        dispatch({
          type: "SET_CURRENT_ACTIVE_SUBSCRIPTION",
          payload: subscription,
        });
        return subscription;
      } catch (err) {
        dispatch({ type: "SET_ERROR", payload: err });
        return null;
      }
    },
    [estateAppApiInstance]
  );

  const handleGetPaymentRequired = useCallback(
    async (
      ud_id: string | null,
      product: string,
      paymentScope?: "INDIVIDUAL" | "HOUSEHOLD"
    ): Promise<PaymentRequiredData | null> => {
      if (!ud_id) {
        return null;
      }
      try {
        dispatch({ type: "SET_PAYMENT_REQUIRED_LOADING", payload: true });
        // query parameters are from request either SAMPLE or PRO for values

        // Append payment_scope as query parameter if provided
        let productWithParams = product;
        if (paymentScope) {
          productWithParams = `${product}?payment_scope=${paymentScope}`;
        }

        const response = await getPaymentRequired({
          entity_id: productWithParams,
          ud_id: ud_id,
          entity: "payment-required",
        });
        dispatch({ type: "SET_PAYMENT_REQUIRED", payload: response });
        return response;
      } catch (err) {
        dispatch({
          type: "SET_PAYMENT_REQUIRED",
          payload: {
            will_price:
              stripeStoreProps?.household?.professional_group
                ?.default_will_price || 0,
            trust_price:
              stripeStoreProps?.household?.professional_group
                ?.default_trust_price || 0,
            total_amount_paid: 0,
            covered_ud_ids: [],
          },
        });
        dispatch({ type: "SET_ERROR", payload: err });
        return null;
      } finally {
        dispatch({ type: "SET_PAYMENT_REQUIRED_LOADING", payload: false });
      }
    },
    [getPaymentRequired, stripeStoreProps?.household?.professional_group?.default_trust_price, stripeStoreProps?.household?.professional_group?.default_will_price]
  );

  const handleCreateOrUpdatePaymentIntent = useCallback(
    async (
      ud_id: string | null,
      paymentIntentId: string | null,
      product: string,
      paymentScope?: "INDIVIDUAL" | "HOUSEHOLD"
    ) => {
      if (!ud_id) {
        return;
      }

      dispatch({ type: "SET_PAYMENTS_SAVING", payload: true });
      try {
        const params = {
          entity: "payments",
          ud_id: ud_id,
        };
        let response;
        if (paymentIntentId) {
          response = await handleUpdatePayment(
            { entity_id: paymentIntentId, entity: "payments", ud_id: ud_id },
            {
              id: paymentIntentId,
              product: product,
              payment_scope: paymentScope,
            }
          );
        } else {
          response = await handleCreatePayment(params, {
            id: paymentIntentId,
            product: product,
            payment_scope: paymentScope,
          });
        }
        // Handle the response directly
        let paymentIntent;
        if (response && typeof response === "object" && response.data) {
          // Backend returns { data: {...} }
          paymentIntent = response.data;
        } else {
          // Fallback in case the format is different
          paymentIntent = response;
        }
        dispatch({
          type: "SET_CURRENT_PAYMENT_INTENT",
          payload: paymentIntent as StripePaymentIntent,
        });
      } catch (err) {
        dispatch({ type: "SET_ERROR", payload: err });
      } finally {
        dispatch({ type: "SET_PAYMENTS_SAVING", payload: false });
      }
    },
    [handleCreatePayment, handleUpdatePayment]
  );

  const handleClearPaymentIntent = useCallback(() => {
    dispatch({ type: "SET_CURRENT_PAYMENT_INTENT", payload: null });
  }, []);

  const refreshPaymentRequired = useCallback(async () => {
    if (product && stripeStoreProps.ud_id) {
      await handleGetPaymentRequired(stripeStoreProps.ud_id, product);
    }
  }, [product, stripeStoreProps.ud_id, handleGetPaymentRequired]);

  useEffect(() => {
    const paywallOption = stripeStoreProps?.household?.household_role?.paywall_option;
    // Only auto-fetch payment required for 'signup' paywall (not 'document_submission')
    if (
      product &&
      stripeStoreProps.ud_id &&
      paywallOption === 'signup' 
    ) {
      handleGetPaymentRequired(stripeStoreProps.ud_id, product);
    } else if (paywallOption === "direct") {
      // If ON_PRO_TAB, set payment required data to 0 prices
      dispatch({
        type: "SET_PAYMENT_REQUIRED",
        payload: {
          will_price: 0,
          trust_price: 0,
          credit_available: 0,
          total_amount_paid: 0,
          covered_ud_ids: [],
        },
      });
    }
  }, [
    product,
    stripeStoreProps.ud_id,
    stripeStoreProps.household?.household_role?.paywall_option,
    handleGetPaymentRequired,
  ]);

  return {
    ...state,
    handleCreateOrUpdatePaymentIntent,
    handleClearPaymentIntent,
    handleGetPaymentRequired,
    refreshPaymentRequired,
    createSubscription,
    createSetupIntent,
    createTrialSubscription,
    cancelSubscription,
    retrieveSubscription,
  };
};
